"use client"

import { useEffect, useState, type RefObject } from "react"
import Lightbox, { type Slide } from "yet-another-react-lightbox"
import Zoom from "yet-another-react-lightbox/plugins/zoom"
import Captions from "yet-another-react-lightbox/plugins/captions"
import "yet-another-react-lightbox/styles.css"
import "yet-another-react-lightbox/plugins/captions.css"

/**
 * Click-to-zoom for opt-in blog images. content-processor renders zoomable
 * images (data-zoomable) as `<button data-zoomable data-full-src=… data-credit=…>`
 * cards; this component finds those buttons inside `containerRef`, builds a
 * gallery, and opens a zoom+pan lightbox on click. Attribution (data-credit /
 * data-license) becomes the slide caption so CC BY-SA credit is shown in-view.
 *
 * Content is server-rendered as an HTML string (dangerouslySetInnerHTML), so we
 * bind at runtime rather than rendering these buttons in JSX. Rebinds when the
 * post content changes.
 */
export function BlogImageLightbox({
  containerRef,
  contentKey,
}: {
  containerRef: RefObject<HTMLElement | null>
  contentKey?: string
}) {
  const [slides, setSlides] = useState<Slide[]>([])
  const [index, setIndex] = useState(-1)

  useEffect(() => {
    const root = containerRef.current
    if (!root) return

    const buttons = Array.from(
      root.querySelectorAll<HTMLButtonElement>("button[data-zoomable]")
    )
    if (buttons.length === 0) return

    const built: Slide[] = buttons.map((btn) => {
      const img = btn.querySelector("img")
      const credit = btn.getAttribute("data-credit") || ""
      const license = btn.getAttribute("data-license") || ""
      const description = [credit, license].filter(Boolean).join(" · ")
      const title = btn.getAttribute("data-title") || img?.getAttribute("alt") || ""
      return {
        src: btn.getAttribute("data-full-src") || img?.currentSrc || img?.src || "",
        alt: img?.getAttribute("alt") || "",
        title,
        description: description || undefined,
      }
    })
    setSlides(built)

    const handlers = buttons.map((btn, i) => {
      const handler = () => setIndex(i)
      btn.addEventListener("click", handler)
      return { btn, handler }
    })

    return () => {
      for (const { btn, handler } of handlers) btn.removeEventListener("click", handler)
    }
  }, [containerRef, contentKey])

  if (slides.length === 0) return null

  return (
    <Lightbox
      slides={slides}
      open={index >= 0}
      index={index < 0 ? 0 : index}
      close={() => setIndex(-1)}
      on={{ view: ({ index: i }) => setIndex(i) }}
      plugins={[Zoom, Captions]}
      zoom={{ maxZoomPixelRatio: 3, scrollToZoom: true }}
      captions={{ descriptionTextAlign: "center" }}
      carousel={{ finite: slides.length <= 1 }}
      controller={{ closeOnBackdropClick: true }}
      styles={{ container: { backgroundColor: "rgba(0, 0, 0, 0.9)" } }}
    />
  )
}
